import React, { useState, useMemo, useEffect } from 'react';
import { TechnologyNotesModal } from './TechnologyNotesModal';
import { useCustomer } from '../../context/CustomerContext';
import { customerApi } from '../../services/api';
import { toast } from 'react-toastify';

// Interfaces (technology_group_id and technology_id for POST one-item contract)
interface TechnologyDomain {
    id: string;
    name: string;
    technology_group_id?: string;
}

interface Technology {
    id: string;
    name: string;
    domainId: string;
    status: 'In Production' | 'No Usage' | 'Deploying' | 'Optimizing';
    notes?: string;
    technology_id?: string;
}

interface TechnologyTableProps {
    domains: TechnologyDomain[];
    technologies: Technology[];
    customerId?: string;
    keyTechnology?: any[];
}

type SortField = 'domain' | 'name' | 'status';
type SortDirection = 'asc' | 'desc';

export const TechnologyTable: React.FC<TechnologyTableProps> = ({ domains, technologies, customerId, keyTechnology }) => {
    const { updateCustomerData } = useCustomer();
    const [techList, setTechList] = useState<Technology[]>(technologies);

    // Keep techList in sync when parent passes updated technologies (e.g. after tab switch or context refresh)
    useEffect(() => {
        setTechList(technologies);
    }, [technologies]);

    const [sortField, setSortField] = useState<SortField>('domain');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [selectedTech, setSelectedTech] = useState<Technology | null>(null);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // Toggle direction if clicking the same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field and default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Handler to update status (one POST per item per backend contract)
    const handleStatusChange = async (techId: string, newStatus: 'In Production' | 'No Usage' | 'Deploying' | 'Optimizing') => {
        const updated = techList.map((tech) =>
            tech.id === techId ? { ...tech, status: newStatus } : tech
        );
        setTechList(updated);

        if (!customerId) return;
        const updatedTech = updated.find(tech => tech.id === techId);
        const domain = updatedTech ? domains.find(d => d.id === updatedTech.domainId) : null;
        if (!updatedTech || !domain) return;
        const technology_group_id = domain.technology_group_id;
        const technology_id = updatedTech.technology_id;
        if (!technology_group_id || !technology_id) {
            toast.error('Missing technology or domain ID; cannot save.');
            return;
        }
        try {
            await customerApi.createOrUpdateTechnologyItem(customerId, {
                technology_group_id,
                label: domain.name,
                technology_id,
                data: { name: updatedTech.name, status: newStatus, notes: updatedTech.notes ?? '' }
            });
            const technology = await customerApi.getCustomerTechnology(customerId);
            if (technology) {
                updateCustomerData({ technology });
            }
            toast.success('Technology status updated successfully!');
        } catch (error) {
            console.error('Error updating technology status:', error);
            toast.error('Failed to update technology status.');
        }
    };

    // Helper to render sort indicator
    const renderSortIndicator = (field: SortField) => {
        if (sortField !== field) {
            return (
                <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12L6 17L11 12M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        }
        
        return sortDirection === 'asc' ? (
            <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ) : (
            <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12L6 17L11 12" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    };

    // Group by domain and sort within groups
    const groupedTechnologies = useMemo(() => {
        // First, group technologies by domain
        const domainGroups = domains.map((domain) => {
            const domainTechnologies = techList.filter((tech) => tech.domainId === domain.id);
            return {
                domain,
                technologies: domainTechnologies,
            };
        }).filter(group => group.technologies.length > 0);

        // Sort technologies within each domain group
        domainGroups.forEach((group) => {
            group.technologies.sort((a, b) => {
                if (sortField === 'name') {
                    return sortDirection === 'asc' 
                        ? a.name.localeCompare(b.name)
                        : b.name.localeCompare(a.name);
                }
                
                if (sortField === 'status') {
                    return sortDirection === 'asc' 
                        ? a.status.localeCompare(b.status)
                        : b.status.localeCompare(a.status);
                }
                
                // Default: sort by name if sorting by domain
                return a.name.localeCompare(b.name);
            });
        });

        // If sorting by domain, also sort the domain groups themselves
        if (sortField === 'domain') {
            domainGroups.sort((a, b) => {
                return sortDirection === 'asc' 
                    ? a.domain.name.localeCompare(b.domain.name)
                    : b.domain.name.localeCompare(a.domain.name);
            });
        }

        return domainGroups;
    }, [techList, sortField, sortDirection, domains]);

    return (
        <div className="w-full overflow-x-auto">
            <table className="w-full border-collapse border">
                <thead>
                    <tr className="text-left bg-transparent/5">
                        <th className="py-4 px-6 font-medium text-[#0F2A4F] w-1/3">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('domain')}
                            >
                                Domain
                                {renderSortIndicator('domain')}
                            </div>
                        </th>
                        <th className="py-4 px-6 font-medium text-[#0F2A4F] w-1/4">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('name')}
                            >
                                Technology
                                {renderSortIndicator('name')}
                            </div>
                        </th>
                        <th className="py-4 px-6 font-medium text-[#0F2A4F] w-1/4">
                            <div 
                                className="flex justify-between items-center cursor-pointer" 
                                onClick={() => handleSort('status')}
                            >
                                Status
                                {renderSortIndicator('status')}
                            </div>
                        </th>
                        <th className="py-4 px-6 font-medium text-[#0F2A4F] w-1/3">
                            Vendor Notes
                        </th>
                    </tr>
                </thead>
                <tbody className='text-sm'>
                    {groupedTechnologies.map((group) => (
                        <React.Fragment key={group.domain.id}>
                            {group.technologies.map((tech, index) => (
                                <tr key={tech.id} className="border-t border-gray-200">
                                    {index === 0 ? (
                                        <td
                                            className="py-4 px-6 align-top font-semibold text-[#002D72] italic border-x"
                                            rowSpan={group.technologies.length}
                                        >
                                            {group.domain.name}
                                        </td>
                                    ) : null}
                                    <td className="py-2 px-6 border-x">{tech.name}</td>
                                    <td
                                        className={`py-2 px-6 border-x ${
                                            tech.status === 'In Production' ? 'bg-[#E3F7E2]' : 
                                            tech.status === 'Deploying' ? 'bg-[#FFF4E5]' : 
                                            tech.status === 'Optimizing' ? 'bg-[#E3F0FF]' : 
                                            'bg-[#FAE7E8]'
                                        }`}
                                    >
                                        <select
                                            value={tech.status}
                                            onChange={(e) =>
                                                handleStatusChange(tech.id, e.target.value as 'In Production' | 'No Usage' | 'Deploying' | 'Optimizing')
                                            }
                                            className="bg-transparent focus:outline-none w-full"
                                        >
                                            <option value="In Production">In Production</option>
                                            <option value="Deploying">Deploying</option>
                                            <option value="Optimizing">Optimizing</option>
                                            <option value="No Usage">No Usage</option>
                                        </select>
                                    </td>
                                    <td className="py-2 px-6 border-x">
                                        <button
                                            onClick={() => {
                                                setSelectedTech(tech);
                                                setIsNotesModalOpen(true);
                                            }}
                                            className="text-[#0F2A4F] hover:text-blue-800 focus:outline-none"
                                        >
                                            {tech.notes ? (
                                                <span className="text-sm">{tech.notes}</span>
                                            ) : (
                                                <span className="text-sm text-gray-500 italic">+ Add Notes</span>
                                            )}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>

            {selectedTech && (
                <TechnologyNotesModal
                    isOpen={isNotesModalOpen}
                    onClose={() => {
                        setIsNotesModalOpen(false);
                        setSelectedTech(null);
                    }}
                    onSave={async (notes) => {
                        try {
                            if (!customerId || !selectedTech) return;
                            const domain = domains.find(d => d.id === selectedTech.domainId);
                            const technology_group_id = domain?.technology_group_id;
                            const technology_id = selectedTech.technology_id;
                            if (!technology_group_id || !technology_id) {
                                toast.error('Missing technology or domain ID; cannot save notes.');
                                return;
                            }
                            await customerApi.createOrUpdateTechnologyItem(customerId, {
                                technology_group_id,
                                label: domain!.name,
                                technology_id,
                                data: { name: selectedTech.name, status: selectedTech.status, notes }
                            });
                            const updatedTechList = techList.map(tech =>
                                tech.id === selectedTech.id ? { ...tech, notes } : tech
                            );
                            setTechList(updatedTechList);
                            const technology = await customerApi.getCustomerTechnology(customerId);
                            if (technology) {
                                updateCustomerData({ technology });
                            }
                            toast.success('Technology notes updated successfully!');
                            setIsNotesModalOpen(false);
                            setSelectedTech(null);
                        } catch (error) {
                            console.error('Error saving notes:', error);
                            toast.error('Failed to update notes.');
                        }
                    }}
                    notes={selectedTech.notes || ''}
                    techName={selectedTech.name}
                />
            )}
        </div>
    );
};
